import React, { useMemo } from "react";
import { createPortal } from "react-dom";
import styled, { createGlobalStyle } from "styled-components";
import {
  Modal,
  colors2,
  UI_BODY_SEMIBOLD,
  ButtonPrimaryV2,
  ButtonSecondaryV2,
  ButtonGhostV2,
} from "oolib";
import { globalScrollbarStyling } from "oolib/dist/v2/themes/globalStyles";
import { FieldTypeList } from "../FieldTypeList";
import { SM_UIContent } from "@/_self_managed/SM_UIContent";
import { WarningNotice } from "@/components/infoBoxes/WarningNotice";

/**
 * "Verify Mapping" modal (Figma: node 22252-41667). Sits between "Confirm
 * Import" and the name-the-dataset step (ContentTypeTitleModal): the user
 * reviews — and edits — the field type auto-detected for each imported column,
 * then commits with "Finish Import".
 *
 * Unlike GettingStartedModal (which lists the same fields READ-ONLY as an
 * intro), here the field list is rendered in EDIT mode (FieldTypeList
 * readOnly={false}) so the types are changeable in place.
 *
 * - Finish Import: proceed to the name-the-dataset modal.
 * - Go Back:       dismiss and return to the preview/config table (X + backdrop
 *                  click do the same).
 */

/**
 * Own backdrop, same split as GettingStartedModal + ImportModalShell: Modal is
 * passed hideBackdrop (keeps click-outside-to-close, drops its black tint) and
 * we render the design's tint here instead.
 */
const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: #0013320d;
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
  z-index: 999999998;
`;

/**
 * oolib's Modal exposes no style hook for its card, but it deliberately sets
 * className="StyledModalLargeContentWrapper" on it, and the card portals to
 * #modal-root. Reach it from here to apply the design's drop shadow. Only
 * mounted while this modal is (no other oolib Modal renders alongside it in this
 * flow), so it's effectively scoped to it.
 */
const ModalShadowStyle = createGlobalStyle`
  #modal-root .StyledModalLargeContentWrapper {
    box-shadow: 0px 4px 20px 0px #00000026;
  }
`;

const HEADER_HEIGHT = 44; // per the design's header frame

/**
 * Modal is passed overflowVisible so it never scrolls itself; the field list is
 * the only scroll container. It stays inside Modal's 90dvh surface cap less this
 * modal's fixed chrome: the 44px header, the ~58px footer (14px padding either
 * side of a 30px button) and the body's 14px padding top and bottom. When the
 * no-date-field notice shows above the list, its height (two lines of helptext
 * plus padding, ~52px, and the body's 14px gap) comes off the list too.
 */
const FIELD_LIST_MAX_HEIGHT = `calc(90dvh - 150px)`;
const FIELD_LIST_MAX_HEIGHT_WITH_NOTICE = `calc(90dvh - 216px)`;

const Header = styled.div`
  height: ${HEADER_HEIGHT}px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 10px 10px 14px;
  border-bottom: 1px solid ${colors2.grey10};
`;

// Per the design the body is just the field list — no explanatory left column
// like GettingStartedModal has.
const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 14px;
`;

// Field comps that give the platform a timestamp per document. Kept in step
// with the BE list (directImportFieldTypeOptions.js 'date' / 'dateTime').
const DATE_FIELD_COMPS = new Set(["DatePicker", "DateTimePicker"]);

// The one scroll container in the modal.
const FieldListScrollArea = styled.div`
  max-height: ${({ $withNotice }) =>
    $withNotice ? FIELD_LIST_MAX_HEIGHT_WITH_NOTICE : FIELD_LIST_MAX_HEIGHT};
  overflow-y: auto;
  ${globalScrollbarStyling}
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  padding: 14px;
  border-top: 1px solid ${colors2.grey10};
`;

const CloseButton = styled(ButtonGhostV2)`
  padding: 4px;
  margin-right: -4px;
`;

export const VerifyMappingModal = ({
  outline = [],
  options = [],
  onFieldChange,
  onGoBack,
  onContinue,
  // What Continue actually does differs by flow — on a reimport this modal is
  // the last step before the import fires, so the caller labels it with the
  // same CTA the first-import flow puts on its final form.
  continueText = "Continue",
  // First imports create the dataset, so this is the one moment to nudge the
  // user towards a date field before the schema is fixed. Reimports add rows to
  // an existing dataset whose fields are already set, so the nudge is moot.
  isFirstImport = false,
}) => {
  // Columns marked "Don't Import" carry enabled: false and never become fields.
  const hasDateField = useMemo(
    () =>
      (outline || []).some(
        (f) => f.enabled !== false && DATE_FIELD_COMPS.has(f.comp)
      ),
    [outline]
  );
  const showNoDateFieldWarning = isFirstImport && !hasDateField;

  return (
    <>
      <ModalShadowStyle />
      {createPortal(<Backdrop />, document.getElementById("modal-root"))}
      <Modal
        title="Verify Mapping"
        onClose={onGoBack}
        disableHeader
        fitToContentHeight
        overflowVisible
        hideBackdrop
        showActionPanel={false}
        desktopWidth="47.9rem"
      >
        <Header>
          <UI_BODY_SEMIBOLD color={colors2.grey90}>
            Verify Mapping
          </UI_BODY_SEMIBOLD>
          <CloseButton icon="X" onClick={onGoBack} />
        </Header>

        <Body>
          {showNoDateFieldWarning && (
            <WarningNotice>
              {SM_UIContent.SM_DirectDataImport.bannerAlert.noDateField}
            </WarningNotice>
          )}
          <FieldListScrollArea $withNotice={showNoDateFieldWarning}>
            <FieldTypeList
              outline={outline}
              readOnly={false}
              options={options}
              onFieldChange={onFieldChange}
            />
          </FieldListScrollArea>
        </Body>

        <Footer>
          <ButtonSecondaryV2 onClick={onGoBack}>Go Back</ButtonSecondaryV2>
          <ButtonPrimaryV2 onClick={onContinue}>{continueText}</ButtonPrimaryV2>
        </Footer>
      </Modal>
    </>
  );
};

export default VerifyMappingModal;
